import React, { useState, useEffect, useCallback } from 'react'
import { useParams } from 'react-router-dom'
import { Listbox } from '@headlessui/react'
import { ChevronUpDownIcon, MagnifyingGlassIcon, FunnelIcon, CalendarIcon } from '@heroicons/react/24/outline'
import AttachmentCard from '@/components/AttachmentCard'
import { 
  getProjectAttachments, 
  getProjectUploaders, 
  getAttachmentStats,
  AttachmentWithDetails, 
  AttachmentFilters,
  UploaderInfo,
  AttachmentStats
} from '@/services/api/attachment'

const AttachmentsPage: React.FC = () => {
  const { projectKey } = useParams<{ projectKey: string }>()
  const [attachments, setAttachments] = useState<AttachmentWithDetails[]>([])
  const [uploaders, setUploaders] = useState<UploaderInfo[]>([])
  const [stats, setStats] = useState<AttachmentStats | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  
  // 篩選狀態
  const [filters, setFilters] = useState<AttachmentFilters>({
    search: '',
    uploaderId: undefined,
    fileType: undefined,
    page: 1,
    limit: 20
  })
  
  // 分頁狀態
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 20,
    total: 0,
    totalPages: 0
  })

  // 檔案類型選項
  const fileTypeOptions = [
    { value: undefined, label: '所有類型' },
    { value: 'image' as const, label: '圖片' },
    { value: 'video' as const, label: '影片' },
    { value: 'document' as const, label: '文件' }
  ]

  // 日期篩選選項
  const dateFilterOptions = [
    { value: 'all', label: '所有時間' },
    { value: 'today', label: '今天' },
    { value: 'yesterday', label: '昨天' },
    { value: 'last7days', label: '過去 7 天' },
    { value: 'last30days', label: '過去 30 天' },
    { value: 'thisMonth', label: '本月' },
    { value: 'thisYear', label: `今年 (${new Date().getFullYear()})` },
    { value: 'lastYear', label: `去年 (${new Date().getFullYear() - 1})` }
  ]

  const [selectedDateFilter, setSelectedDateFilter] = useState<string>('all')

  // 根據選擇的日期篩選計算日期範圍
  const calculateDateRange = (filterType: string) => {
    const now = new Date()
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate())
    
    switch (filterType) {
      case 'today':
        return {
          startDate: today.toISOString().split('T')[0],
          endDate: new Date(today.getTime() + 24 * 60 * 60 * 1000 - 1).toISOString().split('T')[0]
        }
      case 'yesterday': {
        const yesterday = new Date(today.getTime() - 24 * 60 * 60 * 1000)
        return {
          startDate: yesterday.toISOString().split('T')[0],
          endDate: yesterday.toISOString().split('T')[0]
        }
      }
      case 'last7days': {
        const sevenDaysAgo = new Date(today.getTime() - 7 * 24 * 60 * 60 * 1000)
        return {
          startDate: sevenDaysAgo.toISOString().split('T')[0],
          endDate: today.toISOString().split('T')[0]
        }
      }
      case 'last30days': {
        const thirtyDaysAgo = new Date(today.getTime() - 30 * 24 * 60 * 60 * 1000)
        return {
          startDate: thirtyDaysAgo.toISOString().split('T')[0],
          endDate: today.toISOString().split('T')[0]
        }
      }
      case 'thisMonth': {
        const firstDayOfMonth = new Date(now.getFullYear(), now.getMonth(), 1)
        return {
          startDate: firstDayOfMonth.toISOString().split('T')[0],
          endDate: today.toISOString().split('T')[0]
        }
      }
      case 'thisYear': {
        const firstDayOfYear = new Date(now.getFullYear(), 0, 1)
        return {
          startDate: firstDayOfYear.toISOString().split('T')[0],
          endDate: today.toISOString().split('T')[0]
        }
      }
      case 'lastYear': {
        const firstDayOfLastYear = new Date(now.getFullYear() - 1, 0, 1)
        const lastDayOfLastYear = new Date(now.getFullYear() - 1, 11, 31)
        return {
          startDate: firstDayOfLastYear.toISOString().split('T')[0],
          endDate: lastDayOfLastYear.toISOString().split('T')[0]
        }
      }
      default:
        return { startDate: undefined, endDate: undefined }
    }
  }

  // 載入附件資料
  const loadAttachments = useCallback(async () => {
    if (!projectKey) return
    
    try {
      setLoading(true)
      
      // 根據選擇的日期篩選計算日期範圍
      const dateRange = calculateDateRange(selectedDateFilter)
      const filtersWithDate = {
        ...filters,
        startDate: dateRange.startDate,
        endDate: dateRange.endDate
      }
      
      const response = await getProjectAttachments(projectKey, filtersWithDate)
      
      if (response.success) {
        setAttachments(response.data?.attachments ?? [])
        if (response.data?.pagination) {
          setPagination(response.data.pagination)
        }
      } else {
        setError('載入附件失敗')
      }
    } catch (err) {
      setError('載入附件時發生錯誤')
      console.error('Error loading attachments:', err)
    } finally {
      setLoading(false)
    }
  }, [projectKey, filters, selectedDateFilter])

  // 載入上傳者列表
  const loadUploaders = useCallback(async () => {
    if (!projectKey) return
    
    try {
      const response = await getProjectUploaders(projectKey)
      if (response.success) {
        setUploaders(response.data?.uploaders ?? [])
      }
    } catch (err) {
      console.error('Error loading uploaders:', err)
    }
  }, [projectKey])

  // 載入統計資料
  const loadStats = useCallback(async () => {
    if (!projectKey) return
    
    try {
      const response = await getAttachmentStats(projectKey)
      if (response.success) {
        setStats(response.data?.stats ?? null)
      }
    } catch (err) {
      console.error('Error loading stats:', err)
    }
  }, [projectKey])

  // 初始載入
  useEffect(() => {
    loadUploaders()
    loadStats()
  }, [loadUploaders, loadStats])

  // 當篩選條件變更時重新載入
  useEffect(() => {
    loadAttachments()
  }, [loadAttachments])

  // 搜尋處理
  const handleSearchChange = (value: string) => {
    setFilters(prev => ({ ...prev, search: value, page: 1 }))
  }

  // 上傳者篩選處理
  const handleUploaderChange = (uploaderId: string | undefined) => {
    setFilters(prev => ({ ...prev, uploaderId, page: 1 }))
  }

  // 檔案類型篩選處理
  const handleFileTypeChange = (fileType: 'image' | 'video' | 'document' | undefined) => {
    setFilters(prev => ({ ...prev, fileType, page: 1 }))
  }

  // 分頁處理
  const handlePageChange = (page: number) => {
    setFilters(prev => ({ ...prev, page }))
  }

  // 清除篩選條件
  const clearFilters = () => {
    setFilters({
      search: '',
      uploaderId: undefined,
      fileType: undefined,
      page: 1,
      limit: 20
    })
    setSelectedDateFilter('all')
  }

  if (error) {
    return (
      <div className="flex-1 flex items-center justify-center">
        <div className="text-red-600">{error}</div>
      </div>
    )
  }

  return (
    <div className="p-6">
      {/* 統計資訊 */}
      {stats && (
        <div className="mb-6 bg-white rounded-lg border border-gray-200 p-4">
          <div className="flex space-x-6 text-sm">
            <div className="text-center">
              <div className="text-2xl font-bold text-blue-600">{stats.total}</div>
              <div className="text-gray-600">總計</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-green-600">{stats.byType.image}</div>
              <div className="text-gray-600">圖片</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-purple-600">{stats.byType.video}</div>
              <div className="text-gray-600">影片</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-orange-600">{stats.byType.document}</div>
              <div className="text-gray-600">文件</div>
            </div>
          </div>
        </div>
      )}

      {/* 搜尋與篩選區域 */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 mb-6">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {/* 搜尋框 */}
          <div className="relative">
            <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
            <input
              type="text"
              placeholder="搜尋檔案名稱..."
              value={filters.search || ''}
              onChange={(e) => handleSearchChange(e.target.value)}
              className="w-full pl-10 pr-3 py-2 border border-gray-200 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>

          {/* 上傳者篩選 */}
          <Listbox value={filters.uploaderId} onChange={handleUploaderChange}>
            <div className="relative">
              <Listbox.Button className="w-full bg-white border border-gray-200 rounded-md px-3 py-2 text-left focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent">
                <span className="block truncate">
                  {filters.uploaderId 
                    ? uploaders.find(u => u.id === filters.uploaderId)?.name || '選擇上傳者'
                    : '所有上傳者'
                  }
                </span>
                <ChevronUpDownIcon className="absolute right-2 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
              </Listbox.Button>
              <Listbox.Options className="absolute z-10 mt-1 w-full bg-white border border-gray-200 rounded-md shadow-lg max-h-60 overflow-auto">
                <Listbox.Option value={undefined} className="cursor-pointer select-none relative py-2 px-3 hover:bg-gray-50">
                  所有上傳者
                </Listbox.Option>
                {uploaders.map((uploader) => (
                  <Listbox.Option
                    key={uploader.id}
                    value={uploader.id}
                    className="cursor-pointer select-none relative py-2 px-3 hover:bg-gray-50"
                  >
                    <div className="flex items-center space-x-2">
                      <div className="w-6 h-6 bg-gray-300 rounded-full flex items-center justify-center text-xs">
                        {uploader.avatar ? (
                          <img
                            src={uploader.avatar}
                            alt={uploader.name}
                            className="w-full h-full rounded-full object-cover"
                          />
                        ) : (
                          uploader.name.charAt(0).toUpperCase()
                        )}
                      </div>
                      <span>{uploader.name}</span>
                    </div>
                  </Listbox.Option>
                ))}
              </Listbox.Options>
            </div>
          </Listbox>

          {/* 檔案類型篩選 */}
          <Listbox value={filters.fileType} onChange={handleFileTypeChange}>
            <div className="relative">
              <Listbox.Button className="w-full bg-white border border-gray-200 rounded-md px-3 py-2 text-left focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent">
                <span className="block truncate">
                  {fileTypeOptions.find(option => option.value === filters.fileType)?.label}
                </span>
                <ChevronUpDownIcon className="absolute right-2 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
              </Listbox.Button>
              <Listbox.Options className="absolute z-10 mt-1 w-full bg-white border border-gray-200 rounded-md shadow-lg">
                {fileTypeOptions.map((option) => (
                  <Listbox.Option
                    key={option.label}
                    value={option.value}
                    className="cursor-pointer select-none relative py-2 px-3 hover:bg-gray-50"
                  >
                    {option.label}
                  </Listbox.Option>
                ))}
              </Listbox.Options>
            </div>
          </Listbox>

          {/* 日期篩選 */}
          <Listbox value={selectedDateFilter} onChange={setSelectedDateFilter}>
            <div className="relative">
              <Listbox.Button className="w-full bg-white border border-gray-200 rounded-md px-3 py-2 text-left focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent">
                <span className="flex items-center">
                  <CalendarIcon className="w-4 h-4 mr-2 text-gray-400" />
                  <span className="block truncate">
                    {dateFilterOptions.find(option => option.value === selectedDateFilter)?.label || '所有時間'}
                  </span>
                </span>
                <ChevronUpDownIcon className="absolute right-2 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
              </Listbox.Button>
              <Listbox.Options className="absolute z-10 mt-1 w-full bg-white border border-gray-200 rounded-md shadow-lg max-h-60 overflow-auto">
                {dateFilterOptions.map((option) => (
                  <Listbox.Option
                    key={option.value}
                    value={option.value}
                    className="cursor-pointer select-none relative py-2 px-3 hover:bg-gray-50"
                  >
                    {option.label}
                  </Listbox.Option>
                ))}
              </Listbox.Options>
            </div>
          </Listbox>
        </div>

        {/* 清除篩選按鈕 */}
        <div className="mt-4 flex justify-end">
          <button
            onClick={clearFilters}
            className="flex items-center space-x-2 px-3 py-2 text-sm text-gray-600 hover:text-gray-900 hover:bg-gray-50 rounded-md transition-colors"
          >
            <FunnelIcon className="w-4 h-4" />
            <span>清除篩選</span>
          </button>
        </div>
      </div>

      {/* 附件網格 */}
      {loading ? (
        <div className="flex-1 flex items-center justify-center py-12">
          <div className="text-gray-500">載入中...</div>
        </div>
      ) : attachments.length === 0 ? (
        <div className="flex-1 flex items-center justify-center py-12">
          <div className="text-center text-gray-500">
            <div className="text-6xl mb-4">📎</div>
            <div className="text-lg mb-2">沒有找到附件</div>
            <div className="text-sm">嘗試調整搜尋條件或篩選設定</div>
          </div>
        </div>
      ) : (
        <>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {attachments.map((attachment) => (
              <AttachmentCard
                key={attachment.id}
                attachment={attachment}
              />
            ))}
          </div>

          {/* 分頁 */}
          {pagination.totalPages > 1 && (
            <div className="mt-8 flex items-center justify-center space-x-2">
              <button
                onClick={() => handlePageChange(pagination.page - 1)}
                disabled={pagination.page === 1}
                className="px-3 py-2 text-sm border border-gray-200 rounded-md disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
              >
                上一頁
              </button>
              
              <div className="flex space-x-1">
                {Array.from({ length: pagination.totalPages }, (_, i) => i + 1).map(page => (
                  <button
                    key={page}
                    onClick={() => handlePageChange(page)}
                    className={`px-3 py-2 text-sm border rounded-md ${
                      page === pagination.page
                        ? 'bg-blue-600 text-white border-blue-600'
                        : 'border-gray-200 hover:bg-gray-50'
                    }`}
                  >
                    {page}
                  </button>
                ))}
              </div>

              <button
                onClick={() => handlePageChange(pagination.page + 1)}
                disabled={pagination.page === pagination.totalPages}
                className="px-3 py-2 text-sm border border-gray-200 rounded-md disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
              >
                下一頁
              </button>
            </div>
          )}
        </>
      )}
    </div>
  )
}

export default AttachmentsPage
